import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import CheckCircle from '@mui/icons-material/CheckCircleOutlined';
import RadioButtonUnchecked from '@mui/icons-material/RadioButtonUncheckedOutlined';
import { useT, TextKey } from '../../i18n';
import { getCompanyCode } from '../../utils/company';
import { AuthUserData, getResourcePermissions } from '../../utils/permission-utils';

type StepKey = 'office' | 'employee' | 'collect' | 'lendType' | 'client';

/** Orden en que conviene hacerlo: cada paso usa lo del anterior (la ruta necesita sede y representante). */
const STEPS: { key: StepKey; route: string; create: string }[] = [
  { key: 'office', route: '/admin/offices', create: '/admin/offices/new' },
  { key: 'employee', route: '/admin/sales-reps', create: '/admin/sales-reps/new' },
  { key: 'collect', route: '/admin/routes', create: '/admin/routes/new' },
  { key: 'lendType', route: '/admin/loan-types', create: '/admin/loan-types/new' },
  { key: 'client', route: '/admin/clients', create: '/admin/clients/new' },
];

const hiddenKey = () => `ruttia.onboarding.hidden.${getCompanyCode() ?? 'default'}`;

/**
 * Primeros pasos de una empresa nueva, en el panel principal: qué falta configurar, en orden, con un
 * botón a cada pantalla y la guía. Desaparece cuando todo está hecho; también se puede ocultar.
 */
const OnboardingCard: React.FC = () => {
  const t = useT();
  const navigate = useNavigate();
  const [done, setDone] = useState<Record<StepKey, boolean> | null>(null);
  const [user, setUser] = useState<AuthUserData | null>(null);
  const [hidden, setHidden] = useState(() => {
    try {
      return window.localStorage.getItem(hiddenKey()) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (hidden) return;
    let alive = true;
    Promise.all([axios.get('onboarding'), axios.get('user')])
      .then(([status, me]) => {
        if (!alive) return;
        setDone(Object.fromEntries((status.data?.steps ?? []).map((s: { key: StepKey; done: boolean }) => [s.key, s.done])) as Record<StepKey, boolean>);
        setUser(me.data?.data ?? null);
      })
      .catch(() => alive && setDone(null));
    return () => {
      alive = false;
    };
  }, [hidden]);

  if (hidden || !done) return null;
  const completed = STEPS.filter((s) => done[s.key]).length;
  if (completed === STEPS.length) return null;
  const next = STEPS.find((s) => !done[s.key])?.key;

  const hide = () => {
    try {
      window.localStorage.setItem(hiddenKey(), '1');
    } catch {
      /* se oculta solo en esta visita */
    }
    setHidden(true);
  };

  return (
    <Card variant="outlined" sx={{ mb: 3 }} data-tour="home-onboarding">
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 1, justifyContent: 'space-between' }}>
          <Typography component="h2" variant="h6">
            {t('onboarding.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('onboarding.progress', { done: completed, total: STEPS.length })}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
          {t('onboarding.intro')}
        </Typography>
        <LinearProgress variant="determinate" value={(completed / STEPS.length) * 100} sx={{ mt: 2, mb: 1, height: 6, borderRadius: 3 }} aria-hidden />

        <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
          {STEPS.map((s, i) => {
            const isDone = done[s.key];
            const canCreate = getResourcePermissions(user, s.route).canCreate;
            return (
              <Box
                component="li"
                key={s.key}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'auto minmax(0, 1fr)', sm: 'auto minmax(0, 1fr) auto' },
                  columnGap: 1.5,
                  rowGap: 1,
                  alignItems: 'center',
                  py: 1.5,
                  borderTop: i ? 1 : 0,
                  borderColor: 'divider',
                }}
              >
                {isDone ? <CheckCircle color="success" aria-label={t('onboarding.done')} /> : <RadioButtonUnchecked color={s.key === next ? 'primary' : 'disabled'} />}
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600, textDecoration: isDone ? 'line-through' : 'none', color: isDone ? 'text.secondary' : 'text.primary' }}>
                    {t(`onboarding.${s.key}.title` as TextKey)}
                  </Typography>
                  {!isDone && (
                    <Typography variant="body2" color="text.secondary">
                      {t(`onboarding.${s.key}.text` as TextKey)}
                    </Typography>
                  )}
                </Box>
                {!isDone && (
                  <Box sx={{ gridColumn: { xs: '2', sm: 'auto' } }}>
                    <Button
                      variant={s.key === next ? 'contained' : 'outlined'}
                      size="small"
                      disabled={!canCreate}
                      title={canCreate ? undefined : t('onboarding.noPermission')}
                      onClick={() => navigate(s.create)}
                    >
                      {t(`onboarding.${s.key}.action` as TextKey)}
                    </Button>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
          <Button size="small" onClick={() => navigate('/ayuda/primeros-pasos')}>
            {t('onboarding.guide')}
          </Button>
          <Button size="small" color="inherit" onClick={hide}>
            {t('onboarding.hide')}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default OnboardingCard;
